import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { useUploadDocuments } from "./query";
import type { ApBulkUploadResponse, ApDocumentType } from "./types";
import { AP_TAB_LABEL, documentTypeLabel } from "./utils";

/** Mirrors the server-side Ap::Attachment::SUPPORTED_CONTENT_TYPES. */
const ACCEPTED_CONTENT_TYPES =
  "application/pdf,image/jpeg,image/png,image/tiff";

const skippedSummary = (skipped: ApBulkUploadResponse["skipped"]) =>
  skipped
    .map((item) => `${item.file_name ?? "File"}: ${item.error}`)
    .join("\n");

/**
 * "Upload invoices" / "Upload receipts" button backed by a hidden multi-select
 * file input. The upload says what the files are, so the api parses them as
 * that kind without classifying them. The
 * mutation picks the endpoint by file count (single create vs bulk_upload) and
 * always resolves to the bulk response shape; parsing is async, so success
 * toasts say "queued", not "created".
 */
export const UploadInvoicesButton = ({
  businessId,
  documentType = "invoice",
  /** Steps down to the outline pill when the page has a louder primary action. */
  variant = "default",
}: {
  businessId?: string | null;
  documentType?: ApDocumentType;
  variant?: "default" | "secondary";
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadInvoices, isPending } = useUploadDocuments(
    businessId,
    documentType,
  );
  const kind = documentTypeLabel(documentType);
  const tabLabel =
    AP_TAB_LABEL[documentType === "receipt" ? "receipts" : "invoices"];

  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    uploadInvoices(files, {
      onSuccess: ({ queued, skipped }) => {
        if (skipped.length === 0) {
          toast.success(
            queued.length === 1
              ? `${kind.capitalized} queued for parsing`
              : `${queued.length} ${kind.lower}s queued for parsing`,
            {
              // Deliberately "new invoices", not "your invoices": a duplicate
              // or failed parse may add nothing, so no specific row promised.
              description: `New ${kind.lower}s will appear under ${tabLabel} in a minute or two.`,
            },
          );
        } else {
          toast.warning(
            `${queued.length} of ${queued.length + skipped.length} ${kind.lower}s queued for parsing`,
            { description: skippedSummary(skipped) },
          );
        }
      },
      onError: (error) => {
        // Single-file failures carry the specific reason in the message. On
        // a multi-file all-skipped 422 only the aggregate "0 of N queued"
        // survives the ApiError transform — per-file reasons are lost.
        toast.error(`Couldn't upload ${kind.lower}s. Please try again.`, {
          description: getErrorMessage(error),
        });
      },
    });
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_CONTENT_TYPES}
        className="hidden"
        onChange={(event) => {
          handleFiles(event.target.files);
          // Reset so picking the same file again re-triggers onChange.
          event.target.value = "";
        }}
      />
      <Button
        variant={variant}
        onClick={() => inputRef.current?.click()}
        disabled={isPending}
        aria-busy={isPending || undefined}
      >
        {!isPending && <UploadSimpleIcon data-icon="inline-start" />}
        Upload {kind.lower}s
      </Button>
    </>
  );
};
